import axios from "axios";
import React, { createContext, Dispatch, SetStateAction, useContext, useState } from "react";
import { getAllPersonnelWithFiltering } from "../api/PersonnelApi";
import { getUmpireGuests } from "../api/UmpireGuestsApi";
import { useAlert } from "./AlertsContext";

type UmpirePersonnelContext = {
    umpirePersonnel: Array<any>;
    setUmpirePersonnel: Dispatch<SetStateAction<any[]>>;
    personnelAdvancedSearch: (
        arg1: any,
        arg2: Array<number>,
        arg3: Array<number>,
        arg4: string,
        arg5: string,
        arg6: number,
        arg7: boolean,
        cancelSource: any,
    ) => void;
    updatePersonnel: (updatedPersonnel: any) => void;
    personnelLoaded: boolean;
    umpireGuestsSearch: (arg1: number, arg2: number, arg4: any) => void;
};

const UmpirePersonnelContext = createContext<UmpirePersonnelContext>({
    umpirePersonnel: [],
    setUmpirePersonnel: null,
    personnelAdvancedSearch: null,
    updatePersonnel: null,
    personnelLoaded: null,
    umpireGuestsSearch: null,
});

const UmpirePersonnelContextProvider: React.FC = ({ children }) => {
    const showAlert = useAlert();
    const [umpirePersonnel, setUmpirePersonnel] = useState<any[]>([]);
    const [personnelLoaded, setPersonnelLoaded] = useState<boolean>(true);

    const updatePersonnel = (updatedPersonnel: any) => {
        const oldPersonnelIndex = umpirePersonnel.findIndex(
            personnel => personnel.id === updatedPersonnel.id,
        );
        if (oldPersonnelIndex > 0) {
            const newAdminPersonnel = [...umpirePersonnel];
            newAdminPersonnel.splice(oldPersonnelIndex, 0, updatedPersonnel);
            setUmpirePersonnel([...newAdminPersonnel]);
        }
    };

    const personnelAdvancedSearch = (
        searchText: any,
        orgIds: Array<number>,
        statuses: Array<number>,
        personnelType: string,
        bgcScore: string,
        seasonId: number,
        searchInactive: boolean,
        cancelSource: any,
    ) => {
        const personnelSearch = {
            searchText: searchText,
            orgIds: orgIds,
            statuses: statuses,
            personnelType: personnelType,
            bgcScore: bgcScore,
            seasonId: seasonId,
            searchInactive: searchInactive,
        };
        setPersonnelLoaded(false);
        getAllPersonnelWithFiltering(personnelSearch, cancelSource)
            .then(response => {
                // This is for the BestTable to disable the expand/collapse
                response.forEach((entity: any) => (entity.disabled = entity.locked));
                setUmpirePersonnel(response);
                setPersonnelLoaded(true);
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    showAlert(`Error retrieving personnel, Please contact BIGS Systems.`, `danger`);
                }
            });
    };

    const umpireGuestsSearch = (umpireStatsId: number, seasonId: number, cancelSource: any) => {
        setPersonnelLoaded(false);
        getUmpireGuests(umpireStatsId, seasonId, cancelSource)
            .then(response => {
                setUmpirePersonnel(response);
                setPersonnelLoaded(true);
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    showAlert(
                        `Error retrieving umpire guests, Please contact BIGS Systems.`,
                        `danger`,
                    );
                }
            });
    };

    return (
        <UmpirePersonnelContext.Provider
            value={{
                umpirePersonnel,
                setUmpirePersonnel,
                personnelAdvancedSearch,
                updatePersonnel,
                personnelLoaded,
                umpireGuestsSearch,
            }}
        >
            {children}
        </UmpirePersonnelContext.Provider>
    );
};

const useUmpirePersonnel = (): UmpirePersonnelContext => {
    const context = useContext<UmpirePersonnelContext>(UmpirePersonnelContext);
    if (context === undefined) {
        throw new Error(`useUmpirePersonnel must be used within a UmpirePersonnelContextProvider`);
    }
    return context;
};

export { UmpirePersonnelContextProvider, useUmpirePersonnel };
